import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import {
  RouterProvider,
  createRouter,
  ToOptions,
  NavigateOptions,
} from '@tanstack/react-router';
import '@maia/i18n';

import { routeTree } from './routeTree.gen';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from 'react-aria-components';
import { getEnvironment } from './utils/environment/environmentCheck';
import { EnvironmentBanner, ErrorPage } from '@maia/ui';
import { Logger } from '@maia/logger';

const environment = getEnvironment();

Logger.init({
  dsn: 'https://90b2cb03a7953d16f486344747fdda6c@o4509282296922112.ingest.de.sentry.io/4509282299084880',
  environment: environment,
  useMock: environment === 'local' || environment === 'unset',
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: false,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: () => (
    <ErrorPage
      title="Pagina niet gevonden"
      description="Oops, deze pagina kunnen wij niet meer vinden of bestaat niet meer"
      action={{
        type: 'link',
        to: '/dashboard',
        title: 'Terug naar dashboard',
      }}
    />
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare module 'react-aria-components' {
  interface RouterConfig {
    href: ToOptions['to'] | string;
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}

// async function enableMocking() {
//   if (process.env.NODE_ENV !== 'development') {
//     return;
//   }
//   const { worker } = await import('./mocks/browser');
//   return worker.start();
// }

function App() {
  return (
    <StrictMode>
      <I18nProvider locale="NL-nl">
        <QueryClientProvider client={queryClient}>
          {environment !== 'production' && (
            <EnvironmentBanner environment={environment} />
          )}

          <RouterProvider router={router} />
        </QueryClientProvider>
      </I18nProvider>
    </StrictMode>
  );
}

const rootElement = document.getElementById('root') as HTMLElement;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: (error) => {
      Logger.captureException(error, 'error');
    },
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: (error) => {
      Logger.captureException(error, 'error');
    },
    // Callback called when React automatically recovers from errors.
    onRecoverableError: (error) => {
      Logger.captureException(error, 'error');
    },
  });

  // enableMocking().then(() => {
  root.render(<App />);
  // });
}
